import { Box, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { colors } from '../../components';
import { AutopayInfoWrapper, LinearProgressWrapper } from '../../components/global-styles/GlobalStyles';
import CommonIconAvatar from '../../components/ui-components/CommonIconAvatar';
import CommonStatusChip from '../../components/ui-components/StatusChipComponent';
import { ContractApiResponse } from '../../services/contract-services';
import { RootState } from '../../store';
import { trackContractSelected } from '../../utils/google-analytics';
import { NavigationConfig } from '../../utils/navigation-constants';
import { useTranslation } from '../../utils/translations';

import { ContractPaymentInfoRow, NoContractsFound } from './contractListShared';

export interface ClosedListItem {
    id: string;
    contractId?: string;
    type?: string;
    assetDesc?: string;
    lastPayment?: string;
    currentPaymentDue?: string;
    currentPaymentDueDate?: string;
    isCurrentDueActual?: boolean;
    isPastDueActual?: boolean;
    nextPayment?: string;
    amountLabel?: string;
    amount?: string;
    apiContract?: Partial<ContractApiResponse> | null;
}

interface ClosedListProps {
    contractList: ClosedListItem[];
    isFetching?: boolean;
}

const ClosedList: React.FC<ClosedListProps> = ({ contractList, isFetching = false }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const isLoading = useSelector((state: RootState) => state.app.activeHTTPRequests > 0);

    const handleToProceed = (contract: ClosedListItem) => {
        // Pass the full API contract data directly from the stored data
        if (contract.apiContract) {
            trackContractSelected(
                contract.apiContract.AspireId || String(contract.apiContract.Id ?? ''),
                'closed_list',
            );
            navigate(NavigationConfig.ContractDetails, { state: { contract: contract.apiContract } });
        }
    };

    return (
        <Stack height={'100%'}>
            <Grid container spacing={2}>
                {contractList?.length > 0
                    ? contractList?.map(contract => {
                          // Get data from original API contract
                          const apiContract = contract.apiContract;
                          const principleAmount = Number(apiContract?.PrincipleAmount) || 0;
                          const outstandingBalance = Number(apiContract?.OutstandingBalance) || 0;
                          const totalPaid = principleAmount - outstandingBalance;
                          const paymentPercentage = principleAmount > 0 ? (totalPaid / principleAmount) * 100 : 0;
                          return (
                              <Grid
                                  size={{ xs: 12, lg: 6 }}
                                  key={contract.id}
                                  onClick={() => handleToProceed(contract)}
                                  sx={{ cursor: 'pointer' }}
                              >
                                  <Stack
                                      sx={{
                                          borderRadius: 3,
                                          position: 'relative',
                                          overflow: 'hidden',
                                          p: 2,
                                          gap: 2.5,
                                          border: `1px solid ${colors.greyPreset[30]}`,
                                      }}
                                  >
                                      {/* Header Row */}
                                      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                          <Box display="flex" alignItems="start" gap={1.5}>
                                              <Stack direction="row" alignItems="center" gap={1.5}>
                                                  <CommonIconAvatar icon="icon-contracts" />
                                                  <Box>
                                                      <Stack direction={'row'} gap={1.5} alignItems={'center'}>
                                                          <Typography
                                                              variant="subtitle1"
                                                              component="h2"
                                                              fontWeight={600}
                                                          >
                                                              {contract.contractId}
                                                          </Typography>
                                                          <CommonStatusChip
                                                              label={contract.type ?? ''}
                                                              chipBgColor={colors.errorPreset[10]}
                                                          />
                                                      </Stack>
                                                      <Typography
                                                          variant="caption"
                                                          color="textSecondary"
                                                          component="h3"
                                                      >
                                                          {contract.assetDesc ?? ''}
                                                      </Typography>
                                                  </Box>
                                              </Stack>
                                          </Box>

                                          <AutopayInfoWrapper direction="row" alignItems="center" mb={1}>
                                              <span className="icon-info-circle" />
                                              <Typography variant="body2">
                                                  {t('contracts.details.clickForDetails') ||
                                                      'Click the contract to view additional details.'}
                                              </Typography>
                                          </AutopayInfoWrapper>
                                      </Box>
                                      {/* Status + Progress Bar */}
                                      <Box>
                                          <Typography
                                              variant="caption"
                                              color="textSecondary"
                                              display="flex"
                                              justifyContent="space-between"
                                              pb={1}
                                              alignItems="center"
                                          >
                                              {t('contracts.details.paymentProgress')}
                                              <Typography component={'span'} variant="caption" color="textPrimary">
                                                  {apiContract?.PaymentsReceived ?? ''} {t('contracts.list.of')}{' '}
                                                  {apiContract?.NumberOfPayments ?? ''}{' '}
                                                  {t('contracts.list.paymentsMade')}
                                              </Typography>
                                          </Typography>
                                          <LinearProgressWrapper variant="determinate" value={paymentPercentage} />
                                      </Box>

                                      {/* Payment Info */}
                                      <Box
                                          display="grid"
                                          sx={{
                                              gridTemplateColumns: 'repeat(2, 1fr)',
                                              columnGap: { xs: 2, sm: 4 },
                                              rowGap: 2,
                                          }}
                                      >
                                          <ContractPaymentInfoRow
                                              currentPaymentDueDate={contract.currentPaymentDueDate}
                                              currentPaymentDue={contract.currentPaymentDue}
                                              isCurrentDueActual={contract.isCurrentDueActual}
                                              isPastDueActual={contract.isPastDueActual}
                                              nextPayment={contract.nextPayment}
                                              amountLabel={contract.amountLabel}
                                              amount={contract.amount}
                                              lastPayment={contract.lastPayment}
                                          />
                                      </Box>
                                  </Stack>
                              </Grid>
                          );
                      })
                    : !isLoading && !isFetching && (
                          <NoContractsFound
                              message={t('contracts.list.noClosedContractsFound')}
                              headingComponent="h1"
                          />
                      )}
            </Grid>
        </Stack>
    );
};

export default ClosedList;
